import { useState, useEffect } from "react";
import { GetCountries, GetState, GetCity } from "react-country-state-city";
import styled from "styled-components";
import "react-country-state-city/dist/react-country-state-city.css";
import { getProvinceCodeFromName, getCountryCodeFromName, getCountryNameFromCode } from "../../../helpers/useFunctions";

const LocationSelector = (props) => {
  const {
    updatedCountry,
    updatedProvince,
    updatedCity,
    setUpdatedCountry,
    setUpdatedProvince,
  } = props;
  const [countryid, setCountryid] = useState("");
  const [stateid, setStateid] = useState(0);
  const [countriesList, setCountriesList] = useState([]);
  const [cityid, setCityid] = useState(0);
  const [stateList, setStateList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [countrySelected, setCountrySelected] = useState(false);

  useEffect(() => {
    GetCountries().then((result) => {
      const prioritizedCountries = result.filter(
        (country) => country.id === 39 || country.id === 233
      );
      const otherCountries = result.filter(
        (country) => country.id !== 39 && country.id !== 233
      );
      setCountriesList([...prioritizedCountries, ...otherCountries]);
    });
  }, []);

  useEffect(() => {
    if (!countriesList.length) return undefined;

    let isCurrent = true;
    const savedCountry = (updatedCountry || "").trim();
    const savedProvince = (updatedProvince || "").trim();
    const country = countriesList.find((item) =>
      item.isoCode?.toUpperCase() === savedCountry.toUpperCase() ||
      item.iso2?.toUpperCase() === savedCountry.toUpperCase() ||
      item.name.toLowerCase() === savedCountry.toLowerCase()
    );

    if (!country) {
      setCountryid("");
      setStateid(0);
      setStateList([]);
      setCountrySelected(false);
      return undefined;
    }

    setCountryid(country.id);
    GetState(country.id).then((states) => {
      if (!isCurrent) return;

      setStateList(states);
      const state = states.find((item) =>
        item.isoCode?.toUpperCase() === savedProvince.toUpperCase() ||
        item.state_code?.toUpperCase() === savedProvince.toUpperCase() ||
        item.name.toLowerCase() === savedProvince.toLowerCase()
      );

      if (!state) {
        setStateid(0);
        return;
      }

      setStateid(state.id);
      setCountrySelected(true);
      GetCity(country.id, state.id).then((cities) => {
        if (!isCurrent) return;
        setCityList(cities);
        const city = cities.find((item) => item.name === updatedCity);
        if (city) setCityid(city.id);
      });

      const countryCode = country.isoCode || country.iso2 || getCountryCodeFromName(country.name);
      if (countryCode && countryCode !== savedCountry) {
        setUpdatedCountry(countryCode);
      }
      const provinceCode = state.isoCode || state.state_code || getProvinceCodeFromName(state.name, country.name);
      if (provinceCode && provinceCode !== savedProvince) {
        setUpdatedProvince(provinceCode);
      }
    });

    return () => {
      isCurrent = false;
    };
  }, [
    countriesList,
    updatedCountry,
    updatedProvince,
    updatedCity,
    setUpdatedCountry,
    setUpdatedProvince,
  ]);
  return (
    <div className="flex flex-row gap-5 justify-between">
      <div className=" flex flex-col gap-2">
        <label>
          <Labelspan>COUNTRY</Labelspan>
        </label>
        <select
          className="form-select mb-2"
          onChange={(e) => {
            const country = countriesList.find(
              (x) => x.id.toString() === e.target.value
            );
            if (!country) return;
            // Store country code instead of name
            // Try isoCode first (from react-country-state-city), then fallback to helper function
            const countryCode = country.isoCode || country.iso2 || getCountryCodeFromName(country.name);
            props.setUpdatedCountry(countryCode || country.name); // Fallback to name if code not found
            props.setUpdatedProvince("");
            if (country.id !== countryid) {
              setStateList([]);
              GetState(country.id).then(setStateList);
            }
            setCountryid(country.id);
            setCountrySelected(true);
            setStateid(0);
          }}
          value={countrySelected ? countryid : ""}
          disabled={props.isDisabled}
        >
          <option value="">Select Country</option>
          {countriesList.map((item, index) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-2">
        <label>
          {" "}
          <Labelspan>PROVINCE / STATE</Labelspan>
        </label>
        <select
          className="form-select mb-2"
          onChange={(e) => {
            const state = stateList.find(
              (x) => x.id.toString() === e.target.value
            ); //here you will get full state object.
            if (!state) {
              setStateid(0);
              props.setUpdatedProvince("");
              return;
            }
            setStateid(state.id);
            setCountrySelected(true);
            // Store province code instead of name
            // Try isoCode first (from react-country-state-city), then fallback to helper function
            const countryCode = props.updatedCountry || "";
            const countryName = getCountryNameFromCode(countryCode) || countriesList.find((item) => item.id === countryid)?.name;
            const provinceCode = state.isoCode || state.state_code || getProvinceCodeFromName(state.name, countryName);
            props.setUpdatedProvince(provinceCode || state.name); // Fallback to name if code not found
            GetCity(countryid, state.id).then((result) => {
              setCityList(result);
            });
          }}
          value={stateid}
          disabled={props.isDisabled || !countrySelected}
        >
          <option key={0} value={""}>
            Select Province / State
          </option>
          {stateList.map((item, index) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      </div>
      {props.showCity && (
        <div className="flex flex-col gap-2">
          <label>
            {" "}
            <Labelspan>CITY</Labelspan>
          </label>
          <select
            className="form-select mb-2"
            onChange={(e) => {
              const city = cityList.find(
                (x) => x.id.toString() === e.target.value
              ); //here you will get full city object.
              if (!city) return;
              setCityid(city.id);
              props.setUpdatedCity(city.name);
            }}
            value={cityid}
            disabled={props.isDisabled}
          >
            <option key={0} value={""}>
              Select City
            </option>
            {cityList.map((item, index) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};
export default LocationSelector;
const Labelspan = styled.span`
  color: #000;
  font-family: "Space Grotesk";
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  letter-spacing: 3.5px;
`;
